import { stringify, parse } from 'qs'
import { DateFactory, translate } from 'rhg-vue-common'
import { assetsUrl } from '@/infrastructure/Configuration'
import OffersFilter from '@/services/offers/OffersFilter'
import { Occupancy } from '@/domain/Occupancy'

export const getQueryParams = query => {
  const index = query.indexOf('#')
  if (index > 0) {
    query = query.substring(0, index)
  }
  return parse(query, { ignoreQueryPrefix: true, format: 'RFC1738' })
}

export function updateURLParams(parameters) {
  const url = addQueryParams(window.location.origin + window.location.pathname, parameters)
  window.history.replaceState({ path: url }, '', url)
}

export function addQueryParams(url, data, hash = window.location.hash) {
  return url + stringify(data, { addQueryPrefix: true, format: 'RFC1738', encode: false }) + hash
}

export const redirect = url => {
  global.UtilsBrowser.redirect(url)
}

export const joinArrayWithComma = array => {
  return array.join(', ')
}

export const formatDate = date => {
  return DateFactory.formatDate(DateFactory.dateObject(date))
}

export function timeout(timeout) {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve('resolved')
    }, timeout)
  })
}

export function isLhgSite() {
  return window.location.hostname.toLowerCase().includes('louvre')
}

export function getExternalBaseUrl() {
  const location = window.location
  const origin = location.origin?.includes('localhost')
    ? 'https://web-dev.louvrehotels.com'
    : location.origin
  return origin.toLowerCase().includes('louvre')
    ? origin.replace('louvre', 'radisson')
    : origin.replace('radisson', 'louvre')
}

export function isMobileDevice() {
  return global.device.mobile() || global.innerWidth < 576
}

export function isMobileOrTablet() {
  return global.device.mobile() || global.device.tablet() || global.innerWidth < 992
}

export const uniqueValues = (value, index, self) => self.indexOf(value) === index

export const isTaxIncludedRegion = () => {
  if (typeof global.ConfigDynamic === 'undefined' || !global.AkamaiService.userCountry()) {
    return
  }
  const taxIncludedRegion = global.ConfigDynamic.valueOf(
    global.configDynamicKey.DEFAULT_TAXES_INCLUDED_COUNTRY_CODES
  )
    .split(',')
    .some(country => country === global.AkamaiService.userCountry())
  const taxExcludedRegion = global.ConfigDynamic.valueOf(
    global.configDynamicKey.DEFAULT_TAXES_EXCLUDED_COUNTRY_CODES
  )
    .split(',')
    .some(country => country === global.AkamaiService.userCountry())
  return (taxIncludedRegion ? true : undefined) || (taxExcludedRegion ? false : undefined)
}

export function buildImageUrl(image, impolicy) {
  if (image) {
    let url = image.url + '?impolicy=' + impolicy
    if (image.gravity) {
      url += '&gravity=' + image.gravity
    }
    return url
  }

  return `${assetsUrl}/img/img-default-room.jpg`
}

export function getPageLanguage() {
  return global.Utils.getPageLanguage()
}

export function media(device) {
  const devices = {
    'mobile-s': 320,
    'mobile-m': 375,
    'mobile-l': 425,
    'mobile-xl': 575,
    tablet: 768,
    'tablet-l': 992,
    laptop: 1024,
    'laptop-l': 1440,
    '4k': 2560
  }

  return devices[device] || 0
}

export const normalizeFilters = filters => {
  const tag = filters?.tags?.length > 0 ? filters.tags : null
  const globalCampaign = filters.globalCampaign
  const codes = filters?.offers?.length > 0 ? filters.offers : ''
  const region = filters?.regions?.length > 0 ? filters.regions[0] : ''
  return new OffersFilter({
    ...filters,
    region,
    codes,
    isRewardsOnly: filters.rewards,
    tag,
    globalCampaign
  })
}

export const hasQueryParam = param => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.has(param)
}

export const getQueryParam = param => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(param)
}

export const scrollEffect = () => {
  const userAgent = navigator.userAgent.toLowerCase()
  if (userAgent.indexOf('firefox') > -1) {
    return 'smooth'
  }
  if (userAgent.indexOf('chrome') > -1) {
    return 'smooth'
  }
  if (userAgent.indexOf('edge') > -1) {
    return 'smooth'
  }
  // edge chromium version
  if (userAgent.indexOf('edg/') > -1) {
    return 'smooth'
  }
  return 'auto'
}

export const showModalChangesSavedSuccess = () => {
  const text = {
    title: window.tridionLabels.ppYourHaveBeenChangesSavedTitle,
    subtitle: window.tridionLabels.ppYourHaveBeenChangesSavedSubtitle
  }

  global.ModalMsg.show(global.$('#modal-success-request-sent'), text, null, null, { show: true })
}

export const parseHtmlEntitiesOnString = str => {
  // this prevents any overhead from creating the object each time
  const element = document.createElement('div')

  if (str && typeof str === 'string') {
    element.innerHTML = str
    str = element.textContent
    element.textContent = ''
  }

  return str
}

export const mapHotelFiltersParams = params =>
  global.$.param(params)
    .replaceAll(/%5B%5D/gi, '')
    .replaceAll('hotelCodes', 'code')
    .replaceAll('cities', 'city')
    .replaceAll('countries', 'country')
    .replaceAll('regions', 'region')
    .replaceAll('brands', 'brand')
    .replaceAll('hotelStatus', 'status')
    .replaceAll('hotelLocations', 'locationType')
    .replaceAll('accessibilityFeatures', 'accessibility')
    .replaceAll('hotelTypes', 'types')
    .replaceAll('hotelServices', 'service')

export function scrollAnimation(element, blockValue, preventScrollIfVisible) {
  const behaviorText = scrollEffect()
  if (!(typeof element?.scrollIntoView === 'function')) {
    return false
  }
  if (preventScrollIfVisible.scrollDirection === 'bottom') {
    if (element?.getBoundingClientRect().bottom > window.innerHeight) {
      element.scrollIntoView({ block: blockValue, behavior: behaviorText })
    }
  } else if (preventScrollIfVisible.scrollDirection === 'top') {
    if (element?.getBoundingClientRect().top < window.scrollY) {
      element.scrollIntoView({ block: blockValue, behavior: behaviorText })
    }
  }
  return false
}

export const showModalRewardsLoginOnEmailAlreadyUsedError = email => {
  global.$('#modal-rewards-login').modal('show')
  global
    .$('#modal-rewards-login .modal-body-nlp__form-top')
    .append(
      `<span class="field__error text-center w-100 mb-5" id="email-already-registered-error">${translate(
        'rwEmailAlreadyUsed1'
      )}</span>`
    )
  global.$('#modal-email-rewards-nlp').val(email)

  const _onHideModal = () => {
    global.$('#email-already-registered-error').remove()
    global.$('#modal-email-rewards-nlp').val('')
    global.$('#modal-rewards-login').off('hide.bs.modal')
  }

  global.$('#modal-rewards-login').on('hide.bs.modal', _onHideModal)
}

export function getDateRanges(dates) {
  if (!dates || dates.length === 0) {
    return
  }

  dates = dates.sort((a, b) => a - b)
  let currentInterval = {
    start: dates[0],
    end: dates[0]
  }

  if (dates.length === 1) {
    return [currentInterval]
  }

  return dates.slice(1, dates.length).reduce((acc, date, index, dates) => {
    if (isNextDayOf(date)) {
      currentInterval.end = date
    } else {
      acc.push({ ...currentInterval })
      currentInterval = { start: date, end: date }
    }

    if (isLastDate(index, dates)) {
      acc.push({ ...currentInterval })
    }

    return acc
  }, [])

  function isLastDate(index, dates) {
    return index === dates.length - 1
  }

  function isNextDayOf(date) {
    return DateFactory.isSame(date, DateFactory.nextDay(currentInterval.end))
  }
}

export const formatOccupancy = occupancy => {
  if (!occupancy) {
    return null
  }
  if (!occupancy.rooms) {
    return null
  }
  const rooms = occupancy.rooms.length
  const { adults, children } = occupancy.rooms.reduce(
    (accum, room) => ({
      adults: accum.adults + room.adults,
      children: accum.children + room.children
    }),
    { adults: 0, children: 0 }
  )

  let childrenString
  if (children > 0) {
    childrenString = translate('sbNumChildren', children, children)
  }

  return `${translate('sbNumRooms', rooms, rooms)} ${translate('sbNumAdults', adults, adults)}${
    childrenString ? ' ' + childrenString : ''
  }`
}

export function boldText(originalText, searchText) {
  if (searchText !== '') {
    const textPosition = getStringIndices(searchText, originalText)
    let currentTextPosition = 0
    let returningText = originalText
    const strongTextLength = 17
    for (const element of textPosition) {
      returningText =
        `${returningText.substring(0, element + currentTextPosition)}<strong>` +
        `${returningText.substring(
          element + currentTextPosition,
          element + currentTextPosition + searchText.length
        )}</strong>` +
        `${returningText.substring(
          element + currentTextPosition + searchText.length,
          originalText.length + currentTextPosition
        )}`
      currentTextPosition = currentTextPosition + strongTextLength
    }
    return returningText
  }
  return originalText
}

export function getStringIndices(searchText, originalText) {
  const searchTextLen = searchText.length
  let startIndex = 0
  let index
  const indices = []

  originalText = originalText.toLowerCase()
  searchText = searchText.toLowerCase()
  index = originalText.indexOf(searchText, startIndex)
  if (index > -1) {
    do {
      indices.push(index)
      startIndex = index + searchTextLen
      index = originalText.indexOf(searchText, startIndex)
    } while (index > -1)
  }
  return indices
}

export function serializeOccupancy(occupancy) {
  return occupancy.rooms.reduce(
    (accum, room) => ({
      adults: [...accum.adults, room.adults],
      children: [...accum.children, room.children]
    }),
    { adults: [], children: [] }
  )
}

export function getOccupancyFromAdultsAndChild(adults, children) {
  if (adults) {
    const rooms = []

    adults.forEach((element, index) => {
      if (adultAndChildFormatOk(element, children, index)) {
        throw new Error('err')
      }
      rooms.push({
        adults: parseInt(element),
        children: children ? (children[index] ? parseInt(children[index]) : 0) : 0
      })
    })
    return Occupancy.fromJSON(rooms)
  } else {
    return Occupancy.default()
  }
}
export function adultAndChildFormatOk(adults, children, index) {
  return adults < 0 || (children ? children[index] < 0 : false)
}

export function convertArrayFieldsToArray(formData) {
  const arrayData = {}
  Object.keys(formData)
    .filter(function(key) {
      return /\[[\d+]\]/.test(key)
    })
    .forEach(function(key) {
      const objectKey = key.replace(/\[[\d+]\]/, '')
      if (!arrayData[objectKey]) {
        arrayData[objectKey] = []
      }
      arrayData[objectKey].push(formData[key])
      delete formData[key]
    })
  return arrayData
}

export const reflect = p =>
  p.then(
    v => ({ v, status: 'fulfilled' }),
    e => ({ e, status: 'rejected' })
  )

export const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

export const getParamsWithValue = urlParams => {
  for (const param in urlParams) {
    urlParams[param] = urlParams[param] === '' ? true : urlParams[param]
  }

  return urlParams
}

export const buildUrl = (relativeUrl, lang = '') => {
  return `${window.location.origin}${lang ? '/' + lang : ''}${relativeUrl}`
}

export const getCustomerTier = customer => {
  return customer.tier || ''
}

export const getPiplCheckboxLabel = (translate, linkPrivacyNotice) => {
  return translate('peCheckboxPIPL', undefined, linkPrivacyNotice)
}

export const getLHGPiplCheckboxLabel = (translate, linkPrivacyNotice) => {
  return translate('peCheckboxLHGPIPL', undefined, linkPrivacyNotice)
}

export const getParams = (...keys) => {
  const urlParams = new URLSearchParams(window.location.search)
  return keys.reduce((params, key) => ({ ...params, [key]: urlParams.get(key) }), {})
}

export const removeParams = (...keys) => {
  const urlParams = new URLSearchParams(window.location.search)
  keys.forEach(key => urlParams.delete(key))
  const hasQueryParams = urlParams.toString() ? `?${urlParams.toString()}` : ''
  const newUrl = `${window.location.origin}${window.location.pathname}${hasQueryParams}`
  window.history.replaceState({}, document.title, newUrl)
  return newUrl
}

export const slugify = value => {
  if (!value) return ''

  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
}

export const buildCid = values => {
  return Object.entries(values)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}:${value}`)
    .join('+')
}

export const decorateUrlWithCid = (url, cid) => {
  if (!url || !cid) return url

  const hashIndex = url.indexOf('#')
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : ''
  const urlWithoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url

  const queryIndex = urlWithoutHash.indexOf('?')
  const base = queryIndex >= 0 ? urlWithoutHash.slice(0, queryIndex) : urlWithoutHash
  const query = queryIndex >= 0 ? urlWithoutHash.slice(queryIndex + 1) : ''

  const queryParams = query
    .split('&')
    .filter(Boolean)
    .filter(param => {
      const [key] = param.split('=')
      return decodeURIComponent(key) !== 'cid'
    })

  queryParams.push(`cid=${cid}`)

  return `${base}?${queryParams.join('&')}${hash}`
}

export function normalizeSourceBrand(value) {
  const rawBrand = String(value || '')
    .toLowerCase()
    .trim()

  if (!rawBrand) return 'lhg'

  if (rawBrand.includes('louvre') || rawBrand === 'lhg') {
    return 'lhg'
  }

  if (rawBrand.includes('radisson') || rawBrand === 'rhg' || rawBrand === 'rh') {
    return 'rh'
  }

  return rawBrand
}

export function trackUtag(payload) {
  try {
    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      window.localStorage.getItem('@debug/analytics') === 'true'
    ) {
      console.log('Utils: utag.link', payload)
    }

    if (typeof global !== 'undefined' && global.utag && typeof global.utag.link === 'function') {
      global.utag.link(payload)
      return
    }

    if (typeof window !== 'undefined' && window.utag && typeof window.utag.link === 'function') {
      window.utag.link(payload)
      return
    }

    console.warn('Utils: utag.link not available', payload)
  } catch (e) {
    console.error('Utils: analytics failed', e)
  }
}

export function trackUtagAsync(payload, timeout = 700) {
  return new Promise(resolve => {
    try {
      if (
        typeof window !== 'undefined' &&
        window.localStorage &&
        window.localStorage.getItem('@debug/analytics') === 'true'
      ) {
        console.log('Utils: utag.link (async)', payload)
      }

      const utagObj =
        typeof global !== 'undefined' && global.utag
          ? global.utag
          : typeof window !== 'undefined'
            ? window.utag
            : undefined

      if (utagObj && typeof utagObj.link === 'function') {
        let finished = false
        const timer = setTimeout(() => {
          if (!finished) {
            finished = true
            resolve()
          }
        }, timeout)

        try {
          utagObj.link(payload, function() {
            if (!finished) {
              finished = true
              clearTimeout(timer)
              resolve()
            }
          })
        } catch (e) {
          clearTimeout(timer)
          try {
            trackUtag(payload)
          } catch (err) {}
          resolve()
        }

        return
      }

      try {
        trackUtag(payload)
      } catch (e) {}
      resolve()
    } catch (e) {
      resolve()
    }
  })
}
